import React, { useState } from "react";
import {
  useRetrieveDiscussion,
  usePostComment,
  useAuthentication,
  useProjectResourcesRetrieval,
} from "@planzone/view-models";
import { project as projectMock } from "@planzone/dto";

import { Props } from "./types";
import { Feed } from "./feed";
import { ChatInput } from "./chatInput";
import { Loader } from "./loader";
import styles from "./styles.scss";

const PAGE_SIZE = 10;
const LOAD_OLDER_COMMENTS_SCROLL_THRESHOLD = 20;

function Comments({ subject, type, canPost, project }: Props) {
  const {
    discussion,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    currentPage,
    canLoadMore,
    retrieveDiscussion,
  } = useRetrieveDiscussion(subject);
  const { account } = useAuthentication();
  const { isRequestPending: isPostCommentRequestPending, postComment } =
    usePostComment(discussion);
  // The mentions of the comments without recipients are looked for among the project members.
  const {
    projectResources,
    isRequestPending: isProjectResourcesRequestPending,
    isRequestSuccess: isProjectResourcesRequestSuccess,
    isRequestFailure: isProjectResourcesRequestFailure,
    retrieveProjectResources,
  } = useProjectResourcesRetrieval({ ...projectMock, uuid: project });
  const resources = projectResources.map(({ resource }) => resource);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveDiscussion({ subject, type, page: 0, size: PAGE_SIZE });
  }, [isRequestSuccess, isRequestFailure, isRequestPending]);

  React.useEffect(() => {
    !!project &&
      !isProjectResourcesRequestPending &&
      !isProjectResourcesRequestSuccess &&
      !isProjectResourcesRequestFailure &&
      retrieveProjectResources({ ...projectMock, uuid: project });
  }, [project]);

  const feedRef = React.useRef<HTMLDivElement>(null);
  const isLoadingOlderComments = React.useRef(false);
  const scrollHeightBeforeLoadingOlderComments = React.useRef(0);

  React.useEffect(() => {
    isRequestSuccess && setIsFirstLoad(false);
  }, [isRequestSuccess]);

  React.useEffect(() => {
    if (!feedRef.current) return;

    if (isLoadingOlderComments.current) {
      feedRef.current.scrollTop +=
        feedRef.current.scrollHeight -
        scrollHeightBeforeLoadingOlderComments.current;
      isLoadingOlderComments.current = false;
    } else {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [discussion.comments.length]);

  const canLoadOlderComments = !isRequestPending && canLoadMore;

  return (
    <div className={styles.comments}>
      <div
        className={styles.feed}
        ref={feedRef}
        onScroll={(e) => {
          const target = e.currentTarget;
          if (
            canLoadOlderComments &&
            !isLoadingOlderComments.current &&
            target.scrollTop <= LOAD_OLDER_COMMENTS_SCROLL_THRESHOLD
          ) {
            isLoadingOlderComments.current = true;
            scrollHeightBeforeLoadingOlderComments.current =
              target.scrollHeight;
            retrieveDiscussion({
              subject,
              type,
              page: currentPage,
              size: PAGE_SIZE,
            });
          }
        }}
      >
        {!isRequestPending || !isFirstLoad ? (
          <Feed
            resources={resources}
            project={project}
            isSubmitting={isPostCommentRequestPending}
            isLoadingOlderComments={isRequestPending && currentPage > 0}
            comments={discussion.comments.sort((current, next) =>
              new Date(current.createdDate) < new Date(next.createdDate)
                ? -1
                : 1,
            )}
            onSubmitReply={(comment) =>
              postComment({
                account,
                comment: { ...comment, author: account.resource },
                discussion,
              })
            }
          />
        ) : (
          <Loader />
        )}
      </div>
      {canPost && (
        <div className={styles.form}>
          <ChatInput
            isSubmitting={isPostCommentRequestPending}
            project={project}
            submit={(comment) => {
              postComment({
                account,
                comment: { ...comment, author: account.resource },
                discussion,
              });
            }}
          />
        </div>
      )}
    </div>
  );
}

export { Comments };
